
    import { Vue, Component, Prop } from 'vue-property-decorator'
    import { TwoFactorStorage, TWOFACTOR_RATE_LIMITS } from '@/app/auth/services/twofactor-storage.service'

    @Component
    export default class ResendCodeLink extends Vue {
        @Prop({ type: String, required: true }) storageKey!: string
        @Prop({ type: Number, default: TWOFACTOR_RATE_LIMITS.RESEND_COOLDOWN_SECONDS }) cooldownSeconds!: number
        @Prop({ type: Boolean, default: false }) loading!: boolean
        @Prop({ type: Boolean, default: false }) disabled!: boolean
        @Prop({ type: String, default: '' }) prefixText!: string
        @Prop({ type: String, default: '' }) linkText!: string

        cooldownActive = false
        cooldownCountdown = 0
        cooldownInterval: number | null = null

        private storage: TwoFactorStorage | null = null

        get computedLinkText(): string {
            return this.linkText || (this.$t('page.twofactor.buttons.resend_code') as string)
        }

        get cooldownFormatted(): string {
            const minutes = Math.floor(this.cooldownCountdown / 60)
            const seconds = this.cooldownCountdown % 60
            return `${minutes}:${seconds.toString().padStart(2, '0')}`
        }

        get canResend(): boolean {
            return !this.loading && !this.disabled && !this.cooldownActive
        }

        mounted() {
            this.storage = new TwoFactorStorage(this.storageKey)
            this.restoreCooldownState()
        }

        beforeDestroy() {
            this.clearCooldownInterval()
        }

        /**
         * Start the cooldown timer after a successful resend
         * This method should be called by the parent component after the resend succeeds
         */
        startCooldown(): void {
            if (!this.storage) return

            this.storage.saveResendCooldown(this.cooldownSeconds)
            this.cooldownActive = true
            this.cooldownCountdown = this.cooldownSeconds

            this.startCountdownTimer()
        }

        /**
         * Restore cooldown state from sessionStorage on mount
         */
        restoreCooldownState(): void {
            if (!this.storage) return

            const state = this.storage.getResendCooldown()
            if (state) {
                this.cooldownActive = true
                this.cooldownCountdown = state.remainingSeconds
                this.startCountdownTimer()
            }
        }

        /**
         * Start the countdown interval timer
         */
        private startCountdownTimer(): void {
            this.clearCooldownInterval()

            this.cooldownInterval = window.setInterval(() => {
                this.cooldownCountdown--
                if (this.cooldownCountdown <= 0) {
                    this.onCooldownExpired()
                }
            }, 1000)
        }

        /**
         * Handle cooldown expiration
         */
        private onCooldownExpired(): void {
            this.cooldownActive = false
            this.cooldownCountdown = 0
            this.clearCooldownInterval()
            if (this.storage) {
                this.storage.removeResendCooldown()
            }
            this.$emit('cooldown-expired')
        }

        /**
         * Clear the countdown interval
         */
        clearCooldownInterval(): void {
            if (this.cooldownInterval) {
                clearInterval(this.cooldownInterval)
                this.cooldownInterval = null
            }
        }

        /**
         * Handle resend click
         */
        onResend(): void {
            if (this.canResend) {
                this.$emit('resend')
            }
        }
    }
